import { Compatibility } from '../helpers/compatibility';
import { EventStreamProvider } from './EventStreamProvider';
import { IStreamSubscription } from './EventStreamSubscription';
import { VideoData, VideoOption } from './VideoData';
import { VideoSprite } from './VideoSprite';

export abstract class VideoDataImpl implements VideoData {
  private readonly _video: HTMLVideoElement;
  private _isCanPlay: boolean = false;
  private _resetted: boolean = false;
  private _sprite: VideoSprite | null = null;
  private _firstFrame: VideoSprite | null = null;
  private _endedSubscription: IStreamSubscription | null = null;
  private _onCanPlaySubscription: IStreamSubscription | null = null;
  private _onSuspendSubscription: IStreamSubscription | null = null;
  private _playPromise: Promise<void> | null = null;
  private _shouldBePlaying: boolean = false;

  protected constructor(video: HTMLVideoElement) {
    this._video = video;
    try {
      this._endedSubscription = EventStreamProvider.subscribeElement(
        this._video,
        'ended',
        (event) => this._onEnded(event)
      );
    } catch (err) {
      this._endedSubscription = null;
    }

    try {
      this._onCanPlaySubscription = EventStreamProvider.subscribeElement(
        this._video,
        'canplay',
        (event) => this._onCanPlay(event)
      );
    } catch (err) {
      this._onCanPlaySubscription = null;
    }

    try {
      this._onSuspendSubscription = EventStreamProvider.subscribeElement(
        this._video,
        'suspend',
        (event) => this._onSuspend(event)
      );
    } catch (err) {
      this._onSuspendSubscription = null;
    }

    this._video.setAttribute('playsinline', '');
  }

  public get video(): HTMLVideoElement {
    return this._video;
  }

  public async preinit(): Promise<void> {
    //TODO: HACK FOR NEW CHROME UPDATE
    this._video.loop = true;
    await this._video.play();
    this.stop();
  }

  public get isCanBePlayed(): boolean {
    return this._isCanPlay;
  }

  private _onCanPlay(_event?: Event): void {
    this._isCanPlay = true;
    if (!this._resetted && !this._firstFrame) {
      this._firstFrame = this.createSprite();
      this.updateSprite(this._firstFrame);
    }
  }

  private _onEnded(_event?: Event): void {
    this._video.pause();
  }

  private _onSuspend(_event?: Event): void {
    console.log('Low battery mod');
    Compatibility.IsLowBatteryModActivated(true);
  }

  private _destroySprites(): void {
    if (this._sprite) {
      this.destroySprite(this._sprite);
      this._sprite = null;
    }

    if (this._firstFrame) {
      this.destroySprite(this._firstFrame);
      this._firstFrame = null;
    }
  }

  public reset(): void {
    this._destroySprites();
    this._resetted = true;
  }

  public async prepareToPlay(): Promise<void> {
    await this.preinit();

    const waitForSeeked = new Promise<void>((resolve) => {
      const onSeeked = () => {
        this._video.removeEventListener('seeked', onSeeked);
        resolve();
      };

      this._video.addEventListener('seeked', onSeeked);
    });

    if (this._video.readyState >= 1) {
      this._video.currentTime = 0;
      await waitForSeeked;
    }
  }

  public play(looped: boolean): void {
    this._video.loop = looped;
    this._shouldBePlaying = true;
    this._playPromise = (this._video.play() ?? Promise.resolve())
      .catch((e) => console.warn(`Exception on playing video, ${e}`))
      .finally(() => {
        this._playPromise = null;
      });
  }

  public stop(): void {
    this._shouldBePlaying = false;
    if (!this._playPromise) {
      this._pauseAndReset();
      return;
    }
    // Defer pause until play() resolves — _shouldBePlaying guards against a
    // subsequent play() call landing before this microtask runs.
    this._playPromise.then(() => {
      if (!this._shouldBePlaying) {
        this._pauseAndReset();
      }
    });
  }

  private _pauseAndReset(): void {
    this._video.pause();
    if (this._video.readyState >= 1) {
      this._video.currentTime = 0;
    }
  }

  public pause(): void {
    if (!this._video.paused) {
      this._video.pause();
    }
  }

  public resume(): void {
    if (this._video.paused) {
      this._video.play().catch((e) => {
        console.warn(`Exception on resuming video, ${e}`);
      });
    }
  }

  public dispose(): void {
    //Memory leak http://stackoverflow.com/questions/3258587/how-to-properly-unload-destroy-a-video-element
    if (!this._video.paused) {
      this._video.pause();
    }

    if (this._endedSubscription) {
      this._endedSubscription.cancel();
      this._endedSubscription = null;
    }

    if (this._onCanPlaySubscription) {
      this._onCanPlaySubscription.cancel();
      this._onCanPlaySubscription = null;
    }

    if (this._onSuspendSubscription) {
      this._onSuspendSubscription.cancel();
      this._onSuspendSubscription = null;
    }

    this._video.src = '';
    this._video.load();
    this._video.remove();

    this._destroySprites();
  }

  public getSprite(): VideoSprite | null {
    if (!this._video) {
      return null;
    }

    if (!this._resetted && !this._firstFrame) {
      this._firstFrame = this.createSprite();
      this.updateSprite(this._firstFrame);
    }

    if (!this._sprite) {
      if (this._video.seeking) {
        //We can get an invalid frame if try to use a video while seeking
        return null;
      }
      this._sprite = this.createSprite();
    }

    if (this._video.seeking) {
      if (this._video.loop) {
        //Check that _spite has been already updated previously
        if (this._sprite.revision) {
          //Return previous frame
          return this._sprite;
        }
      } else if (this._firstFrame) {
        return this._firstFrame;
      }
    }

    this.updateSprite(this._sprite);

    return this._sprite;
  }

  public get videoOptions(): VideoOption[] {
    const sources = Array.from(this._video.querySelectorAll('source'));
    return sources.map((source) => ({
      path: source.src,
      type: source.type,
    }));
  }

  // Builds a hidden <video> with the same sources and playback attributes as
  // the pooled element. The media is already in the browser cache, so the
  // clone becomes playable almost immediately.
  protected cloneVideoElement(): HTMLVideoElement {
    const clone = document.createElement('video');
    clone.crossOrigin = 'anonymous';
    clone.setAttribute('playsinline', '');
    clone.setAttribute('webkit-playsinline', '');
    clone.muted = true;
    clone.autoplay = false;
    clone.controls = false;
    clone.loop = false;
    clone.preload = 'auto';
    clone.hidden = true;
    clone.style.display = 'none';

    for (const option of this.videoOptions) {
      const source = document.createElement('source');
      source.src = option.path;
      source.type = option.type;
      clone.appendChild(source);
    }

    (this._video.parentElement ?? document.body).appendChild(clone);
    clone.load();
    return clone;
  }

  public abstract clone(): VideoData;

  abstract createSprite(): VideoSprite;

  abstract updateSprite(sprite: VideoSprite): void;

  abstract destroySprite(sprite: VideoSprite): void;
}
